import type { DataProvider } from 'react-admin';
import type { DebtSlip, PaymentOrder, Supplier, VatInvoice } from '../types';
import { extractDebtSlip } from './claudeVision';
import { findMatches, selectAutomaticMatch } from './matching';
import { meInvoiceSupplierIdentityKey, syncInvoicesFromMeInvoice, syncSuppliersFromMeInvoice } from './meInvoiceSync';
import { enrichSupplierBankFromMeInvoice } from './meInvoiceBankEnrichment';
import { createPaymentOrderForMatch, findSupplierForInvoice } from './paymentOrderAutomation';
import { getSettings } from './settings';

async function getSlip(dp: DataProvider, id: DebtSlip['id']) {
  return (await dp.getOne<DebtSlip>('debtSlips', { id })).data;
}

async function patchSlip(dp: DataProvider, slip: DebtSlip, patch: Partial<DebtSlip>) {
  const data = { ...slip, ...patch };
  return (await dp.update<DebtSlip>('debtSlips', { id: slip.id, data, previousData: slip })).data;
}

const runningAutomations = new Map<string, Promise<PaymentOrder | undefined>>();

async function executeDebtAutomation(
  dp: DataProvider,
  debtSlipId: DebtSlip['id'],
): Promise<PaymentOrder | undefined> {
  let slip = await getSlip(dp, debtSlipId);
  const settings = await getSettings();
  if (!settings.automationEnabled) return undefined;
  if (slip.automationStatus === 'completed' || slip.status === 'ordered') {
    const { data } = await dp.getList<PaymentOrder>('paymentOrders', {
      pagination: { page: 1, perPage: 1000 }, sort: { field: 'createdAt', order: 'DESC' }, filter: {},
    });
    return data.find((order) => String(order.debtSlipId) === String(slip.id));
  }

  slip = await patchSlip(dp, slip, {
    automationStatus: 'queued',
    automationStep: 'queued',
    automationError: undefined,
    automationAttempts: (slip.automationAttempts || 0) + 1,
    automationStartedAt: slip.automationStartedAt || new Date().toISOString(),
  });

  try {
    if (!slip.supplierName || !slip.totalPayable || slip.aiConfidence == null) {
      if (!slip.imageDataUrl) throw new Error('Đơn nợ chưa có ảnh để AI bóc tách.');
      const imageDataUrl = slip.imageDataUrl;
      slip = await patchSlip(dp, slip, { automationStatus: 'extracting', automationStep: 'extracting' });
      const extracted = await extractDebtSlip(imageDataUrl);
      slip = await patchSlip(dp, slip, {
        supplierName: extracted.supplierName ?? slip.supplierName,
        taxCode: extracted.taxCode ?? slip.taxCode,
        slipNumber: extracted.slipNumber ?? slip.slipNumber,
        slipDate: extracted.slipDate ?? slip.slipDate,
        currentAmount: extracted.currentAmount ?? slip.currentAmount,
        oldDebt: extracted.oldDebt ?? slip.oldDebt,
        totalPayable: extracted.totalPayable ?? slip.totalPayable,
        bankInfoRaw: extracted.bankInfoRaw ?? slip.bankInfoRaw,
        rawText: extracted.rawText ?? slip.rawText,
        aiConfidence: extracted.confidence,
        aiModel: settings.model,
        extractedAt: new Date().toISOString(),
        status: 'extracted',
      });
    }
    if (!slip.supplierName || !slip.totalPayable || slip.totalPayable <= 0) {
      throw new Error('AI chưa đọc đủ tên nhà cung cấp hoặc tổng tiền phải trả.');
    }
    if ((slip.aiConfidence || 0) < settings.aiConfidenceThreshold) {
      throw new Error('Độ tự tin AI chưa đạt ngưỡng tự động.');
    }
    if (!slip.slipDate) throw new Error('Thiếu ngày chứng từ để giới hạn dữ liệu meInvoice.');
    const slipDate = slip.slipDate;
    const totalPayable = slip.totalPayable;

    slip = await patchSlip(dp, slip, {
      automationStatus: 'syncing_meinvoice_suppliers',
      automationStep: 'syncing_meinvoice_suppliers',
    });
    const supplierSync = await syncSuppliersFromMeInvoice(dp);
    if (supplierSync.total === 0) throw new Error('meInvoice không trả về nhà cung cấp nào để đối soát.');
    if (supplierSync.invalid >= supplierSync.total) throw new Error('Toàn bộ nhà cung cấp meInvoice không hợp lệ.');

    slip = await patchSlip(dp, slip, {
      automationStatus: 'syncing_meinvoice_invoices',
      automationStep: 'syncing_meinvoice_invoices',
    });
    await syncInvoicesFromMeInvoice(dp);

    slip = await patchSlip(dp, slip, { automationStatus: 'matching', automationStep: 'matching' });
    const { data: invoices } = await dp.getList<VatInvoice>('vatInvoices', {
      pagination: { page: 1, perPage: 10000 }, sort: { field: 'invoiceDate', order: 'DESC' }, filter: {},
    });
    const candidates = findMatches(slip, invoices, {
      tolerancePct: settings.matchAmountTolerancePct,
      toleranceAbs: settings.matchAmountToleranceAbs,
      dateWindowDays: settings.matchDateWindowDays,
      matchAmount: totalPayable,
    });
    const decision = selectAutomaticMatch(candidates, {
      minScore: settings.autoMatchThreshold,
      minGap: settings.autoMatchGap,
      minNameSimilarity: 0.7,
    });
    if (!decision.match) throw new Error(decision.reason || 'Không thể tự động đối soát hóa đơn.');
    const invoice = decision.match.invoice;
    slip = await patchSlip(dp, slip, {
      matchedInvoiceId: invoice.id,
      matchStatus: 'confirmed',
      matchScore: decision.match.score,
      status: 'matched',
    });

    const { data: suppliers } = await dp.getList<Supplier>('suppliers', {
      pagination: { page: 1, perPage: 10000 }, sort: { field: 'name', order: 'ASC' }, filter: {},
    });
    let supplier = findSupplierForInvoice(suppliers, invoice);
    if (!supplier) throw new Error('Không xác định được nhà cung cấp tương ứng với hóa đơn.');
    if (!supplierSync.verifiedSupplierKeys.includes(meInvoiceSupplierIdentityKey(supplier))) {
      throw new Error('Nhà cung cấp khớp chưa được xác minh trong lần đồng bộ meInvoice hiện tại.');
    }

    slip = await patchSlip(dp, slip, { automationStatus: 'enriching_bank', automationStep: 'enriching_bank' });
    supplier = await enrichSupplierBankFromMeInvoice(dp, invoice, supplier);

    slip = await patchSlip(dp, slip, { automationStatus: 'creating_order', automationStep: 'creating_order' });
    const order = await createPaymentOrderForMatch(dp, slip, invoice, supplier, settings);
    await patchSlip(dp, slip, {
      status: 'ordered',
      automationStatus: 'completed',
      automationStep: 'completed',
      automationError: undefined,
      automationFinishedAt: new Date().toISOString(),
    });
    return order;
  } catch (error: any) {
    const latest = await getSlip(dp, debtSlipId);
    await patchSlip(dp, latest, {
      automationStatus: 'needs_review',
      automationStep: latest.automationStep || latest.automationStatus || 'failed',
      automationError: String(error?.message || error),
      automationFinishedAt: new Date().toISOString(),
    });
    throw error;
  }
}

export function runDebtAutomation(
  dp: DataProvider,
  debtSlipId: DebtSlip['id'],
): Promise<PaymentOrder | undefined> {
  const key = String(debtSlipId);
  const current = runningAutomations.get(key);
  if (current) return current;
  const task = executeDebtAutomation(dp, debtSlipId).finally(() => runningAutomations.delete(key));
  runningAutomations.set(key, task);
  return task;
}
